import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import type { CommunicationListParams } from "@/types/communications";
import type { BookAppointmentInput, CreateContactInput, CreateOpportunityInput } from "@/types/gohighlevel";
import {
  goHighLevelCalendarsQueryKey,
  goHighLevelContactsQueryKey,
  goHighLevelConversationQueryKey,
  goHighLevelConversationsQueryKey,
  goHighLevelMessagesQueryKey,
  goHighLevelOpportunitiesQueryKey,
  goHighLevelService,
} from "@/services/goHighLevelService";

export function useGoHighLevelConversations(
  userId: string | undefined,
  connected: boolean,
  params: CommunicationListParams
) {
  return useQuery({
    queryKey: goHighLevelConversationsQueryKey(userId, params),
    queryFn: () => goHighLevelService.listConversations(params),
    enabled: !!userId && connected,
    staleTime: 5 * 60_000,
  });
}

export function useGoHighLevelConversation(
  conversationId: string | null,
  connected: boolean
) {
  return useQuery({
    queryKey: goHighLevelConversationQueryKey(conversationId ?? undefined),
    queryFn: () => goHighLevelService.getConversation(conversationId!),
    enabled: !!conversationId && connected,
  });
}

export function useGoHighLevelMessages(
  conversationId: string | null,
  connected: boolean,
  params: CommunicationListParams
) {
  return useQuery({
    queryKey: goHighLevelMessagesQueryKey(conversationId ?? undefined, params),
    queryFn: () => goHighLevelService.listMessages(conversationId!, params),
    enabled: !!conversationId && connected,
    staleTime: 2 * 60_000,
  });
}

export function useGoHighLevelContacts(
  userId: string | undefined,
  connected: boolean,
  params: CommunicationListParams
) {
  return useQuery({
    queryKey: goHighLevelContactsQueryKey(userId, params),
    queryFn: () => goHighLevelService.listContacts(params),
    enabled: !!userId && connected,
    staleTime: 5 * 60_000,
  });
}

export function useGoHighLevelOpportunities(
  userId: string | undefined,
  connected: boolean,
  params: CommunicationListParams
) {
  return useQuery({
    queryKey: goHighLevelOpportunitiesQueryKey(userId, params),
    queryFn: () => goHighLevelService.listOpportunities(params),
    enabled: !!userId && connected,
    staleTime: 5 * 60_000,
  });
}

export function useGoHighLevelCalendars(userId: string | undefined, connected: boolean) {
  return useQuery({
    queryKey: goHighLevelCalendarsQueryKey(userId),
    queryFn: () => goHighLevelService.listCalendars(),
    enabled: !!userId && connected,
    staleTime: 10 * 60_000,
  });
}

export function useSendGoHighLevelMessage() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ conversationId, body, type }: { conversationId: string; body: string; type?: string }) =>
      goHighLevelService.sendMessage(conversationId, body, type),
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({
        queryKey: goHighLevelMessagesQueryKey(vars.conversationId, {}),
      });
      void queryClient.invalidateQueries({ queryKey: ["gohighlevel-conversations"] });
    },
  });
}

export function useCreateGoHighLevelContact() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateContactInput) => goHighLevelService.createContact(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["gohighlevel-contacts"] });
    },
  });
}

export function useAddGoHighLevelContactTag() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ contactId, tag }: { contactId: string; tag: string }) =>
      goHighLevelService.addContactTag(contactId, tag),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["gohighlevel-contacts"] });
    },
  });
}

export function useCreateGoHighLevelOpportunity() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: CreateOpportunityInput) => goHighLevelService.createOpportunity(input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["gohighlevel-opportunities"] });
    },
  });
}

export function useBookGoHighLevelAppointment() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ calendarId, input }: { calendarId: string; input: BookAppointmentInput }) =>
      goHighLevelService.bookAppointment(calendarId, input),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ["gohighlevel-calendars"] });
    },
  });
}
